import { FC } from 'react';
import classNames from 'classnames';
import type { BlogPostDocument, BlogPostsSlice } from '@/types';
import { Content } from '@prismicio/client';
import { Container } from '@/components';
import { useFilteredSliceItemsByLocationSlug } from '@/hooks';

import ColumnsVariation from './variations/ColumnsVariation';
import DefaultVariation from './variations/DefaultVariations';
import FullImageVariation from './variations/FullImageVariation';
import HeadingVariation from './variations/HeadingVariation';
import LatestPostVariations from './variations/LatestPostsVariations';

import style from './blog.module.scss';
import { getSlicePadding } from 'utils/utils';

const Blog: FC<IBlogProps> = ({ slice, latestPosts }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);
  const { variation = 'default', primary } = slice;

  if (!primary?.active) return null;

  const VariationComponent =
    BlogPostsByVariation[variation] ?? BlogPostsByVariation.default;

  return (
    <section
      className={classNames(
        style.blogs,
        getSlicePadding(primary?.padding_section),
        {
          ['spacing_bottom']: primary?.bottom_spacing,
          ['background__fixed_cover']: primary?.background_style_fixed_cover,
          ['background__contained']: !primary?.background_style_fixed_cover,
        },
      )}
      style={{
        background: primary?.background_color ?? undefined,
        backgroundImage: primary?.background_image?.url
          ? `url(${primary.background_image.url})`
          : undefined,
        minHeight: `${primary?.min_heigth || 0}vh`,
      }}
    >
      <Container className={classNames(style.blogs__container)}>
        <VariationComponent
          items={filteredItems}
          latestPosts={latestPosts || []}
          primary={primary}
        />
      </Container>
    </section>
  );
};

const BlogPostsByVariation: Record<
  BlogPostsSlice['variation'],
  FC<{ primary: any; items: any; latestPosts: BlogPostDocument[] }>
> = {
  default: DefaultVariation,
  columns: ColumnsVariation,
  fullImageWithShadow: FullImageVariation,
  '3Columns': HeadingVariation,
  latestPosts: LatestPostVariations,
};
export interface IBlogProps {
  slice: Content.BlogPostsSlice;
  latestPosts?: BlogPostDocument[];
}

export default Blog;
